import { Component, signal, inject, OnInit } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-activate',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex items-center justify-center p-6 lg:p-8 font-sans" style="background:#f1f9fe;">
      <div class="w-full max-w-md">
        
        <!-- Logo Section -->
        <div class="flex items-center justify-center mb-8">
          <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
            <i class="ti ti-user-check text-white text-2xl"></i>
          </div>
        </div>

        <div class="bg-white rounded-3xl p-8 shadow-sm relative" style="border:1px solid #EEF2F7;">
          <h1 class="text-2xl font-bold text-slate-900 mb-1">{{ 'newPassword' | t }}</h1>
          <p class="text-slate-500 text-sm mb-6">Hello <span class="text-[#2B9AD2] font-bold">{{ userName() }}</span>, please choose a secure password to activate your field account.</p>

          @if (errorMsg()) {
            <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
              <i class="ti ti-alert-circle text-[18px] shrink-0"></i>{{ errorMsg() }}
            </div>
          }

          @if (successMsg()) {
            <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#ECFDF5; color:#059669; border:1px solid #A7F3D0;">
              <i class="ti ti-circle-check text-[18px] shrink-0"></i>{{ successMsg() }}
            </div>
          }

          @if (isValidToken()) {
            <form (submit)="onSubmit()" class="space-y-5">
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">{{ 'newPassword' | t }}</label>
                <input type="password" [(ngModel)]="password" name="password" placeholder="••••••••" 
                       class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" 
                       style="border-color:#E2E8F0; background:#FAFBFC;"
                       onfocus="this.style.borderColor='#2B9AD2';">
              </div>
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">{{ 'confirm' | t }} {{ 'password' | t }}</label>
                <input type="password" [(ngModel)]="confirmPassword" name="confirmPassword" placeholder="••••••••" 
                       class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" 
                       style="border-color:#E2E8F0; background:#FAFBFC;"
                       onfocus="this.style.borderColor='#2B9AD2';">
              </div>
              <button type="submit" [disabled]="isLoading()" class="w-full h-12 rounded-2xl font-bold text-white shadow-sm hover:opacity-90 transition-all flex items-center justify-center disabled:opacity-50" 
                      style="background:linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                @if (isLoading()) { <i class="ti ti-loader animate-spin mr-2"></i> {{ 'loading' | t }} }
                @else { {{ 'activatingAccount' | t }} }
              </button>
            </form>
          } @else if (!errorMsg()) {
            <div class="flex flex-col items-center py-8">
              <i class="ti ti-loader animate-spin text-3xl text-[#2B9AD2] mb-3"></i>
              <p class="text-slate-400 text-sm italic tracking-tight">Securing your session...</p>
            </div>
          }

          @if (errorMsg()) {
            <div class="mt-6 text-center">
              <a routerLink="/login" class="text-sm font-bold text-[#2B9AD2] hover:underline">{{ 'backToLogin' | t }}</a>
            </div>
          }
        </div>

        <p class="text-center text-xs text-slate-400 mt-8 font-medium italic">
          NZ Plumbing Field Services • Secure Provisioning
        </p>
      </div>
    </div>
  `
})
export class ActivateComponent implements OnInit {
  token = '';
  userName = signal('');
  password = '';
  confirmPassword = '';
  isValidToken = signal(false);
  errorMsg = signal('');
  successMsg = signal('');
  isLoading = signal(false);

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (this.token) {
      this.verifyToken();
    } else {
      this.errorMsg.set('Invalid link. Token is missing from the URL.');
    }
  }

  verifyToken() {
    this.api.verifyActivateToken(this.token).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.userName.set(res.data.name);
          this.isValidToken.set(true);
        } else {
          this.errorMsg.set(res.message || 'The activation link is invalid or has expired.');
        }
      },
      error: () => {
        this.errorMsg.set('Failed to connect to security server. Please check your internet.');
      }
    });
  }

  onSubmit() {
    if (!this.password) {
      this.errorMsg.set('Please choose a password.');
      return;
    }
    if (this.password.length < 6) {
      this.errorMsg.set('Password must be at least 6 characters.');
      return;
    }
    if (this.password !== this.confirmPassword) {
      this.errorMsg.set('Passwords do not match.');
      return;
    }

    this.isLoading.set(true);
    this.errorMsg.set('');

    this.api.submitActivate({ token: this.token, password: this.password }).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.successMsg.set('Account activated! You can now sign in.');
          setTimeout(() => this.router.navigate(['/login']), 2000);
        } else {
          this.errorMsg.set(res.message || 'Activation failed.');
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMsg.set('An unexpected error occurred. Please try again.');
      }
    });
  }
}
